/**
 * First-party analytics client (anonymous, aggregate-only).
 *
 * This module feeds our OWN backend `recordAnalytics` GraphQL mutation, which
 * writes the anonymous AnalyticsClient / AnalyticsActiveDay / AnalyticsDaily
 * tables. No third-party analytics provider is involved.
 *
 * Privacy invariants (see plans/04-analytics-implementation.md):
 * - The anonymous id is a random UUID kept in chrome.storage.local. It is NEVER
 *   tied to an account, email, or Firebase uid.
 * - We send ONLY event names from the backend allowlist — never note/todo/verse
 *   content, never PII.
 * - Default ON with a one-tap opt-out (Settings → Privacy). Truly anonymous +
 *   aggregate, so the design decision is opt-out rather than opt-in.
 *
 * Transport: an UNAUTHENTICATED POST to the GraphQL endpoint (recordAnalytics
 * requires no token by design — anonymous free users are exactly who we count).
 * It must fail open: analytics is never on the critical path of any feature.
 */
import { saveToStorageAsync, loadFromStorageAsync } from './storage';
import { nativePlatform } from '../utils/nativeApp';
import { hasExtensionRuntime, isSafariExtension } from '../utils/browserApi';

// ── Event catalogue (must match the backend ALLOWED_EVENT_NAMES) ───────────────
// Const arrays (not bare unions) so the catalogue is enumerable at runtime:
// src/__tests__/analyticsAllowlistParity.test.ts checks every name below
// against the backend allowlist. Add new names to the backend FIRST and deploy
// it — older backends reject a whole batch over one unknown name.
export const FEATURE_KEYS = [
  'prayer', 'qibla', 'verse', 'voice', 'notes', 'tasks', 'streak', 'planner',
  // Meeting sessions split by capture mode so the funnel can tell tab-capture
  // meetings from mic-only room sessions ('voice' still fires for every recording).
  'voice_meeting_tab', 'voice_meeting_room',
  // Voice ↔ Tasks: spoken task capture / voice day planning.
  'voice_tasks', 'voice_plan',
  // Sub-tasks ("steps"): fired when a step is created (from a note tree or
  // the drawer's add-step row) — the §11 fast-follow decisions key off this.
  'task_steps',
  // Quran Insights drawer opened (Ask / Tafsir / Related verses). 'verse' still
  // fires for the verse widget; this is the AI insights destination itself.
  'insights',
] as const;
export type FeatureKey = typeof FEATURE_KEYS[number];

/** First-session funnel steps (W35 S1) — each fires at most once ever per client. */
export const FIRST_RUN_STEPS = [
  'render',
  'paint',
  'geo_shown',
  'geo_granted',
  'geo_denied',
  // First-run gate (2026-09-19). `geo_shown` stays welded to the REAL OS
  // prompt; these two describe the primer that now stands in front of it, so
  // primer-shown → geo_shown is a measurable step. Fresh-cohort only, no
  // backfill — pre-ship clients never emit them and must not be compared.
  'geo_primer_shown',
  'geo_primer_skipped',
  'signin_shown',
  'signin_dismissed',
  'widget',
] as const;
export type FirstRunStep = typeof FIRST_RUN_STEPS[number];

/** First-run Explore Mode (tour) steps. Unlike first_run there is no once-ever
 *  guard and no fresh-cohort gate: card_opened/try_it fire per interaction,
 *  replayed per replay, the rest once per tour run. */
export const TOUR_STEPS = [
  'welcome_shown',
  'started',
  'skipped',
  'card_opened',
  'try_it',
  'completed',
  'replayed',
] as const;
export type TourStep = typeof TOUR_STEPS[number];

/** In-app upgrade card (2026-10-13). Backend allowlist must deploy first. */
export const UPGRADE_CARD_EVENTS = ['shown', 'clicked', 'dismissed'] as const;
export type UpgradeCardEvent = typeof UPGRADE_CARD_EVENTS[number];

/** Web upgrade page (account.rhemn.com/upgrade) sign-in step, 2026-10-16.
 *  Same backend-first deploy rule. Aggregate counts only — the per-campaign
 *  split lives in Stripe Checkout metadata (source / utmCampaign). */
export const UPGRADE_SIGNIN_EVENTS = ['shown', 'completed'] as const;
export type UpgradeSigninEvent = typeof UPGRADE_SIGNIN_EVENTS[number];

/** iPhone App Store rating prompt (services/reviewPrompt.ts): which trigger
 *  asked. Counts REQUESTS handed to Apple, not sheets shown — Apple reports
 *  nothing back. Backend allowlist must deploy first (same rule as above). */
export const REVIEW_PROMPT_REASONS = ['streak', 'purchase', 'active_day'] as const;
export type ReviewPromptReason = typeof REVIEW_PROMPT_REASONS[number];

const PLAIN_EVENT_NAMES = [
  'app_open',
  'activated',
  'verse_shared',
  'streak_milestone_shared',
  'upgrade_clicked',
  'web_checkout_started',
  'checkout_success',
  'day2_return',
] as const;

export type BackendEventName =
  | typeof PLAIN_EVENT_NAMES[number]
  | `upgrade_card_${UpgradeCardEvent}`
  | `upgrade_signin_${UpgradeSigninEvent}`
  | `feature_used:${FeatureKey}`
  | `first_run:${FirstRunStep}`
  | `tour:${TourStep}`
  | `review_prompt:${ReviewPromptReason}`;

/** Every event name this client can emit (the parity test's input). */
export const BACKEND_EVENT_NAMES: readonly BackendEventName[] = [
  ...PLAIN_EVENT_NAMES,
  ...UPGRADE_CARD_EVENTS.map((e) => `upgrade_card_${e}` as const),
  ...UPGRADE_SIGNIN_EVENTS.map((e) => `upgrade_signin_${e}` as const),
  ...FEATURE_KEYS.map((k) => `feature_used:${k}` as const),
  ...FIRST_RUN_STEPS.map((s) => `first_run:${s}` as const),
  ...TOUR_STEPS.map((s) => `tour:${s}` as const),
  ...REVIEW_PROMPT_REASONS.map((r) => `review_prompt:${r}` as const),
];

// ── Platform (backend ALLOWED_PLATFORMS) ──────────────────────────────────────
// Sent with every batch; the backend stores it once per client and splits the
// first-run funnel dimensions by it. Detection order matters: the iOS/iPadOS
// native shell also has no chrome.runtime, and Safari extensions DO have one.
export function detectPlatform(): 'chrome' | 'safari' | 'ios' | 'ipados' | 'web' {
  const native = nativePlatform();
  if (native) return native; // 'ios' | 'ipados'
  if (isSafariExtension()) return 'safari';
  if (hasExtensionRuntime()) return 'chrome';
  return 'web'; // hosted account pages / local dev
}

const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API_URL;
// Unload-safe transport (backend POST /analytics/beacon, same payload as the
// mutation). Same base-URL derivation as accountPortal.ts.
const BEACON_URL = GRAPHQL_API_URL ? `${GRAPHQL_API_URL.replace(/\/graphql\/?$/, '')}/analytics/beacon` : '';

// Set REACT_APP_TELEMETRY_DEBUG=true to log every queued/flushed event to the
// browser console (open the new-tab page's DevTools to watch them fire).
const DEBUG = process.env.REACT_APP_TELEMETRY_DEBUG === 'true';
function debugLog(...args: unknown[]): void {
  if (!DEBUG) return;
  // eslint-disable-next-line no-console
  console.log('[analytics]', ...args);
}

// Storage keys (chrome.storage.local via storage.ts helpers).
const ANON_ID_KEY = 'analyticsAnonId';
const ENABLED_KEY = 'analyticsEnabled'; // opt-out flag; default true
const ACTIVATED_KEY = 'analyticsActivated'; // fired-once guard for the `activated` event

const RECORD_MUTATION = `mutation RecordAnalytics($anonymousId: ID!, $events: [AnalyticsEventInput!]!, $platform: String) {
  recordAnalytics(anonymousId: $anonymousId, events: $events, platform: $platform) { ok }
}`;

// ── Anonymous id ────────────────────────────────────────────────────────────
// UUID matches the backend's ANONYMOUS_ID_RE (^[A-Za-z0-9_-]{8,64}$).
function newAnonId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // Fallback: random + time, hyphen-separated (still allowlist-safe).
  return `a-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

let anonIdPromise: Promise<string> | null = null;
function getOrCreateAnonId(): Promise<string> {
  if (anonIdPromise) return anonIdPromise;
  anonIdPromise = (async () => {
    const existing = await loadFromStorageAsync<string>(ANON_ID_KEY, '');
    if (existing) return existing;
    const id = newAnonId();
    await saveToStorageAsync(ANON_ID_KEY, id);
    return id;
  })();
  return anonIdPromise;
}

// ── Opt-out (default ON) ──────────────────────────────────────────────────────
// Legacy key from the old PostHog consent banner (removed 2026-06-20). A user who
// clicked "Essential only" stored `analyticsConsent = false`. We must honor that
// prior opt-out instead of silently re-enabling analytics on update.
const LEGACY_CONSENT_KEY = 'analyticsConsent';
let enabledCache: boolean | null = null;

export async function getAnalyticsEnabled(): Promise<boolean> {
  if (enabledCache !== null) return enabledCache;

  // An explicit new-setting choice always wins.
  const explicit = await loadFromStorageAsync<boolean | null>(ENABLED_KEY, null);
  if (explicit !== null) {
    enabledCache = explicit;
    return enabledCache;
  }

  // No new choice yet — migrate a prior opt-out from the old consent banner
  // before falling back to the default-on behavior.
  const legacy = await loadFromStorageAsync<boolean | null>(LEGACY_CONSENT_KEY, null);
  enabledCache = legacy === false ? false : true;
  return enabledCache;
}

export async function setAnalyticsEnabled(enabled: boolean): Promise<void> {
  enabledCache = enabled;
  await saveToStorageAsync(ENABLED_KEY, enabled);
}

// ── Batching ──────────────────────────────────────────────────────────────────
// Coalesce events fired in quick succession into one request. Flush on a short
// debounce, and when the page is hidden/unloaded or about to navigate away
// ("leaving" flush) so a closing new-tab page still delivers its app_open and
// the upgrade page delivers its events before the OAuth relay / Stripe redirect.
//
// Leaving flushes go through navigator.sendBeacon, NOT a keepalive fetch: the
// GraphQL POST needs Content-Type: application/json, which makes it a
// CORS-preflighted request from account.rhemn.com and rhemn-app://local, and a
// preflighted keepalive request issued while the document unloads is not
// reliably delivered (2026-10 finding: zero downstream upgrade-funnel rows).
// A string beacon is text/plain — CORS-simple, no preflight, and the browser
// delivers it past unload.
const MAX_BATCH = 50; // backend MAX_EVENTS_PER_CALL
const FLUSH_DEBOUNCE_MS = 1500;

let queue: BackendEventName[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let unloadHookInstalled = false;

function installUnloadHook(): void {
  if (unloadHookInstalled || typeof document === 'undefined') return;
  unloadHookInstalled = true;
  // visibilitychange:hidden is the reliable "page going away" signal in MV3 pages.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush(true);
  });
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => void flush(true));
  }
}

async function flush(keepalive = false): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  if (!GRAPHQL_API_URL) {
    queue = [];
    return;
  }
  if (!(await getAnalyticsEnabled())) {
    queue = [];
    return;
  }

  const batch = queue.slice(0, MAX_BATCH);
  queue = queue.slice(MAX_BATCH);

  try {
    const anonymousId = await getOrCreateAnonId();
    const platform = detectPlatform();
    if (keepalive && sendViaBeacon({ anonymousId, events: batch.map((name) => ({ name })), platform })) {
      debugLog('beaconed', batch.length, 'events →', BEACON_URL);
    } else {
      await sendViaGraphql(anonymousId, batch, platform, keepalive);
    }
  } catch (err) {
    // Fail open — never break UX for analytics. Dropped batch is acceptable
    // (at-most-once contract; better a lost event than a double-count).
    debugLog('flush failed (dropped)', err);
  }

  // If more events arrived past MAX_BATCH, schedule another flush.
  if (queue.length > 0) scheduleFlush();
}

/** True once the browser has accepted the beacon (it then owns delivery). */
function sendViaBeacon(payload: { anonymousId: string; events: { name: BackendEventName }[]; platform: string }): boolean {
  if (!BEACON_URL || typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
  try {
    // A string body is sent as text/plain;charset=UTF-8 — keep it a string.
    return navigator.sendBeacon(BEACON_URL, JSON.stringify(payload));
  } catch {
    return false;
  }
}

async function sendViaGraphql(
  anonymousId: string,
  batch: BackendEventName[],
  platform: string,
  keepalive: boolean,
): Promise<void> {
  debugLog('flushing', batch, '→', GRAPHQL_API_URL);
  const res = await fetch(GRAPHQL_API_URL as string, {
    method: 'POST',
    // NO Authorization header — recordAnalytics is unauthenticated by design.
    headers: { 'Content-Type': 'application/json' },
    keepalive,
    body: JSON.stringify({
      query: RECORD_MUTATION,
      variables: {
        anonymousId,
        events: batch.map((name) => ({ name })),
        platform,
      },
    }),
  });
  debugLog('flushed', batch.length, 'events — HTTP', res.status);
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => void flush(), FLUSH_DEBOUNCE_MS);
}

async function enqueue(name: BackendEventName): Promise<void> {
  if (!GRAPHQL_API_URL) return;
  if (!(await getAnalyticsEnabled())) return;
  installUnloadHook();
  queue.push(name);
  debugLog('queued', name, `(${queue.length} pending)`);
  scheduleFlush();
}

/**
 * Queue an event that is immediately followed by the page going away (a
 * same-tab redirect, or a new tab taking focus) and send the queue NOW through
 * the unload-safe beacon, instead of waiting out the debounce. Every await on
 * this path resolves from localStorage in microtasks, so the beacon is handed
 * to the browser before a `location.href = …` set in the same task commits.
 */
function enqueueAndSendBeforeLeaving(name: BackendEventName): void {
  void (async () => {
    await enqueue(name);
    await flush(true);
  })();
}

// ── First-session funnel (W35 S1) ─────────────────────────────────────────────
// Only FRESH installs emit first_run events: a client whose anonymousId already
// existed when this build first ran had its real first session before the
// instrumentation shipped, and counting a later session as "first" would poison
// the funnel with false perfect runs. Cohort is decided once, at the first
// trackAppOpen after this build lands, and persisted.
const FIRST_RUN_COHORT_KEY = 'analyticsFirstRunCohort'; // 'fresh' | 'legacy'
const INSTALL_DATE_KEY = 'analyticsInstallDate'; // UTC YYYY-MM-DD of first open
const DAY2_FIRED_KEY = 'analyticsDay2Fired';
const firstRunStepKey = (step: FirstRunStep) => `analyticsFirstRun:${step}`;

function todayUTC(): string {
  return new Date().toISOString().slice(0, 10);
}

let cohortPromise: Promise<'fresh' | 'legacy'> | null = null;
function getFirstRunCohort(): Promise<'fresh' | 'legacy'> {
  if (cohortPromise) return cohortPromise;
  cohortPromise = (async () => {
    const stored = await loadFromStorageAsync<'fresh' | 'legacy' | null>(FIRST_RUN_COHORT_KEY, null);
    if (stored) return stored;
    // Decide once: an anonId minted before this moment means the true first
    // session predates the instrumentation.
    const existingAnonId = await loadFromStorageAsync<string>(ANON_ID_KEY, '');
    const cohort: 'fresh' | 'legacy' = existingAnonId ? 'legacy' : 'fresh';
    await saveToStorageAsync(FIRST_RUN_COHORT_KEY, cohort);
    return cohort;
  })();
  return cohortPromise;
}

/**
 * Record a first-session funnel step. No-ops entirely for legacy clients and
 * repeats — each step fires at most once EVER per client (persistent guard).
 * signin_dismissed additionally requires signin_shown to have fired, so "wall
 * passed" can never exceed "wall shown".
 */
export function trackFirstRun(step: FirstRunStep): void {
  void (async () => {
    if (!GRAPHQL_API_URL) return;
    if (!(await getAnalyticsEnabled())) return;
    if ((await getFirstRunCohort()) !== 'fresh') return;
    if (step === 'signin_dismissed') {
      const shown = await loadFromStorageAsync<boolean>(firstRunStepKey('signin_shown'), false);
      if (!shown) return;
    }
    const fired = await loadFromStorageAsync<boolean>(firstRunStepKey(step), false);
    if (fired) return;
    await saveToStorageAsync(firstRunStepKey(step), true);
    await enqueue(`first_run:${step}`);
  })();
}

/**
 * Record an Explore Mode step. Same `family:step` name format as first_run, and
 * the platform dimension rides on the batch exactly as it does for first_run
 * (flush() sends detectPlatform(); the backend folds it in). Goes through the
 * shared batched queue, never one request per tap: the backend drops a whole
 * call over 60 calls/min per client, so rapid card browsing must coalesce.
 * The opt-out is honored inside enqueue.
 */
export function trackTour(step: TourStep): void {
  void enqueue(`tour:${step}`);
}

// day2_return: the client opened the app on exactly install-date + 1 (UTC).
// Opened day 3+ without a day-2 open → never fires; that absence IS the signal.
async function maybeFireDay2Return(): Promise<void> {
  if (!GRAPHQL_API_URL) return;
  if (!(await getAnalyticsEnabled())) return;
  if ((await getFirstRunCohort()) !== 'fresh') return;

  const today = todayUTC();
  const installDate = await loadFromStorageAsync<string>(INSTALL_DATE_KEY, '');
  if (!installDate) {
    await saveToStorageAsync(INSTALL_DATE_KEY, today);
    return;
  }
  if (await loadFromStorageAsync<boolean>(DAY2_FIRED_KEY, false)) return;
  const next = new Date(`${installDate}T00:00:00.000Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  if (today === next.toISOString().slice(0, 10)) {
    await saveToStorageAsync(DAY2_FIRED_KEY, true);
    await enqueue('day2_return');
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Fire once per app load (new-tab open). */
export function trackAppOpen(): void {
  void enqueue('app_open');
  trackFirstRun('render');
  void maybeFireDay2Return();
}

/**
 * Record use of a core feature. The first feature event ever recorded for this
 * client also emits `activated` (the funnel's activation step), guarded by a
 * persistent flag so it fires at most once per anonymous client.
 */
export function trackFeatureUsed(feature: FeatureKey): void {
  void (async () => {
    await enqueue(`feature_used:${feature}`);
    await maybeFireActivated();
  })();
  // Platform-split twin of `activated` for the first-session funnel (fresh
  // cohort only; `activated` itself stays untouched as the legacy total).
  trackFirstRun('widget');
}

let activatedFired = false;
async function maybeFireActivated(): Promise<void> {
  if (activatedFired) return;
  // Don't consume the once-ever activation guard while opted out (or unconfigured):
  // enqueue('activated') would no-op, but persisting the flag would permanently
  // suppress the event — so a user who opts out, uses a feature, then opts back in
  // would never be counted as activated. Bail before touching the flag.
  if (!GRAPHQL_API_URL) return;
  if (!(await getAnalyticsEnabled())) return;

  const already = await loadFromStorageAsync<boolean>(ACTIVATED_KEY, false);
  if (already) {
    activatedFired = true;
    return;
  }
  activatedFired = true;
  await saveToStorageAsync(ACTIVATED_KEY, true);
  await enqueue('activated');
}

export function trackVerseShared(): void {
  void enqueue('verse_shared');
}

/** A streak milestone card was exported/shared (part of the share-loop K-factor). */
export function trackStreakMilestoneShared(): void {
  void enqueue('streak_milestone_shared');
}

/** The iPhone app asked StoreKit for the rating sheet (dimension = trigger). */
export function trackReviewPrompt(reason: ReviewPromptReason): void {
  void enqueue(`review_prompt:${reason}`);
}

export function trackUpgradeClicked(): void {
  void enqueue('upgrade_clicked');
}

/** In-app upgrade card funnel: shown (once per load) → clicked | dismissed.
 *  A click opens the account page in a new tab (or a native sheet), so it is
 *  sent immediately rather than racing the hidden-page flush. */
export function trackUpgradeCard(event: UpgradeCardEvent): void {
  if (event === 'clicked') {
    enqueueAndSendBeforeLeaving('upgrade_card_clicked');
    return;
  }
  void enqueue(`upgrade_card_${event}`);
}

/** Upgrade page sign-in layer: shown (once per opening) → completed.
 *  `shown` can be followed at once by the Google/Apple relay navigation, and
 *  `completed` by the Stripe redirect, so both are sent before leaving. */
export function trackUpgradeSignin(event: UpgradeSigninEvent): void {
  enqueueAndSendBeforeLeaving(`upgrade_signin_${event}`);
}

/** The web upgrade page is about to redirect to Stripe Checkout. This is the
 *  first-party counter for that moment (telemetry.ts's `checkout_started` is
 *  the same moment and is deliberately NOT mirrored here — one name per
 *  moment, no double count). Sent before the redirect commits. */
export function trackWebCheckoutStarted(): void {
  enqueueAndSendBeforeLeaving('web_checkout_started');
}

/** Stripe returned to /upgrade/success. Fired at most once per browser tab
 *  session so a reload of the success page does not double-count. */
export function trackCheckoutSuccess(): void {
  const key = 'analyticsCheckoutSuccessSent';
  try {
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    }
  } catch {
    /* storage blocked: count it anyway (at most a reload double-count) */
  }
  void enqueue('checkout_success');
}

// Test-only reset.
export function _resetAnalyticsForTests(): void {
  queue = [];
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  anonIdPromise = null;
  enabledCache = null;
  activatedFired = false;
  cohortPromise = null;
}
